import { confirmSignIn, signIn } from "aws-amplify/auth";
import {
	MDBBtn,
	MDBCard,
	MDBCardBody,
	MDBCardTitle,
	MDBCheckbox,
	MDBCol,
	MDBContainer,
	MDBFooter,
	MDBIcon,
	MDBInput,
	MDBRow,
	MDBTabs,
	MDBTabsContent,
	MDBTabsItem,
	MDBTabsLink,
	MDBTabsPane,
} from "mdb-react-ui-kit";
import React, { useEffect, useState } from "react";
import { hotjar } from "react-hotjar";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-input-2";
import { useIndustry } from "../../utils/industryContextHook";
import { captureError } from "../../utils/sentryHelpers";
import LangSelect from "../LangSelect";
import Footer from "./Footer";
import { fetchEvent } from "../../utils/fetchEvent";

const SignIn = ({
	setAuthRoute,
	setIsStepConfirm,
	setEmail,
	setPasswordSignUp,
}) => {
	const { currentIndustry } = useIndustry();
	const { t, i18n } = useTranslation();
	const [loginRegisterActive, setLoginRegisterActive] = useState("login");

	const [error, setError] = useState(null);

	const [username, setUsername] = useState("");
	const [password, setPassword] = useState("");

	const [newPasswordRequired, setNewPasswordRequired] = useState(false);
	const [newPassword, setNewPassword] = useState("");
	const [newAttributes, setNewAttributes] = useState([]);
	const [newAttributesValues, setNewAttributesValues] = useState({});

	// Detect if user is in booking process
	const [bookingProcess, setBookingProcess] = useState(false);
	const [bookingProcessData, setBookingProcessData] = useState({});

	const handleLoginRegisterClick = (value) => {
		setLoginRegisterActive(value);
		if (newPasswordRequired) setNewPasswordRequired(false);
	};

	useEffect(() => {
		const params = new URL(window.location.href).searchParams;
		const username = params.get("email");
		const password = params.get("password");
		if (username && password) {
			console.log("auto login", username, password);
			setUsername(username);
			setPassword(password);

			handleSubmit(null, username, password);
		}
		if (window.location.href.includes("checkout")) {
			setBookingProcess(true);
			setBookingProcessData({
				productName: params.get("productName"),
			});
		}
		if (window.location.href.includes("book-ticket")) {
			setBookingProcess(true);
			loadEvent(params.get("eventId"), params.get("ticketType"));
		}
	}, []);

	const loadEvent = async (eventId, ticketType) => {
		const event = await fetchEvent(eventId);
		if (!event || !event.name) {
			console.error("Event or event name not found");
			return;
		}

		const nameEntry =
			event.name.find((n) => n.language === i18n.language) || event.name[0];
		if (nameEntry && nameEntry.text) {
			setBookingProcessData({
				productName: nameEntry.text,
				...(ticketType && { productDescription: "Ticket" }),
			});
		}
	};

	const handleSubmit = async (e, autoLoginUser, autoLoginPassword) => {
		if (e) e.preventDefault();
		if (!autoLoginUser && !username) {
			setError({ message: t("Please enter your email address.") });
			return;
		}
		if (!autoLoginPassword && !password) {
			setError({ message: t("Please enter your password.") });
			return;
		}
		try {
			hotjar.identify(autoLoginUser || username);
			const { nextStep } = await signIn({
				username: autoLoginUser || username,
				password: autoLoginPassword || password,
			});
			if (
				nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
			) {
				setNewPasswordRequired(true);
				setNewAttributes(nextStep.missingAttributes);
			}
			if (nextStep.signInStep === "CONFIRM_SIGN_UP") {
				setAuthRoute("signUp");
				setIsStepConfirm(true);
				setEmail(username);
				setPasswordSignUp(newPassword || password);
			}
			setError(null);
		} catch (error) {
			captureError(error, {
				feature: "authentication",
				operation: "sign-in",
				consoleMessage: "Error signin in",
				context: { username: autoLoginUser || username },
			});
			hotjar.event("Login failed");
			if (autoLoginUser || username) hotjar.identify(autoLoginUser || username);

			if (
				error ===
				"Temporary password has expired and must be reset by an administrator."
			) {
				setError(
					t("Your password has expired. please use “Forgot password?” to create a new password."),
				);
			} else {
				setError(error);
			}
		}
	};

	const [confirmSignInLoading, setConfirmSignInLoading] = useState(false);

	const handleConfirmSignIn = async () => {
		if (!newPassword) {
			setError({ message: t("Please enter your new password.") });
			return;
		}
		setConfirmSignInLoading(true);
		try {
			const user = await confirmSignIn({
				challengeResponse: newPassword,
				options: {
					userAttributes: newAttributesValues,
				},
			});
			console.log(user);

			setError(null);
			//hotjar.identify(user.username);
			hotjar.event("Confirmed Sign In");
		} catch (error) {
			captureError(error, {
				feature: "authentication",
				operation: "confirm-sign-in",
				consoleMessage: "Error confirming sign in:",
				context: { hasNewPassword: !!newPassword, newAttributes },
			});
			hotjar.event("Confirm Sign In failed");
			setError(error);
		}
		setConfirmSignInLoading(false);
	};

	return (
		<>
			<MDBRow
				className="d-flex align-items-center m-0"
				style={{
					minHeight: "100vh",
					paddingBottom: "5rem",
					backgroundImage:
						"url(https://iec-cp-public.s3.eu-central-1.amazonaws.com/2_2500x488px_lounges_ka_2024.jpg)",
					backgroundSize: "cover",
					backgroundRepeat: "no-repeat",
				}}
			>
				<MDBCol
					size="12"
					className="d-flex align-items-center justify-content-center"
				>
					<MDBCard className="col-12 col-md-8 col-xl-6 col-xxl-4">
						<MDBCardBody className={bookingProcess ? "py-2 px-3" : ""}>

							{bookingProcess && (
								<div className="text-center mb-1">
									<MDBCardTitle className="mb-1" style={{ fontSize: "1rem" }}>{t("Your booking")}:</MDBCardTitle>
									<div className="d-flex align-items-center justify-content-center">
										<div className="d-flex align-items-center text-start">
											<MDBIcon
												style={{ fontSize: "18px" }}
												fas
												icon="shopping-cart"
											/>
											<div className="ms-2">
												<p className="fw-bold mb-0" style={{ fontSize: "0.9rem" }}>
													{bookingProcessData.productName
														? t(bookingProcessData.productName)
														: <span className="placeholder-glow"><span className="placeholder" style={{ width: "120px", display: "inline-block" }}></span></span>
													}
												</p>
												<p className="text-muted mb-0" style={{ fontSize: "0.85rem" }}>
													{bookingProcessData.productDescription
														? t(bookingProcessData.productDescription)
														: <span className="placeholder-glow"><span className="placeholder" style={{ width: "60px", display: "inline-block" }}></span></span>
													}
												</p>
											</div>
										</div>
									</div>
									<div
										className="alert alert-info py-1 mx-auto mt-1 mb-0"
										style={{ width: "fit-content", fontSize: "0.85rem" }}
									>
										<h6 className="mb-0" style={{ fontSize: "0.85rem" }}>{t("Please sign in to continue")}</h6>
									</div>
									<hr className="my-2" />
								</div>
							)}
							<MDBTabs pills justify className="mb-3">
								<MDBTabsItem>
									<MDBTabsLink
										onClick={() => handleLoginRegisterClick("login")}
										active={loginRegisterActive === "login"}
									>
										{t("Login")}
									</MDBTabsLink>
								</MDBTabsItem>
								<MDBTabsItem>
									<MDBTabsLink
										onClick={() => setAuthRoute("signUp")}
										active={loginRegisterActive === "register"}
									>
										{t("Register")}
									</MDBTabsLink>
								</MDBTabsItem>
							</MDBTabs>

							<MDBTabsContent>
								<MDBTabsPane open={loginRegisterActive === "login"}>
									{newPasswordRequired ? (
										<>
											<h4>{t("Please set a new password")}</h4>
											<MDBInput
												className="mb-4"
												type="password"
												id="newPassword"
												label={t("New Password")}
												value={newPassword}
												onChange={(e) => setNewPassword(e.target.value)}
											/>
											{newAttributes.length > 0 && (
												<h6>{t("Please fill in the following fields")}</h6>
											)}

											{newAttributes.map((attribute, index) => {
												if (attribute === "phone_number") {
													return (
														<>
															<span>{t("Phone number")}</span>
															<PhoneInput
																className="mb-4"
																key={index}
																country={"de"}
																enableSearch
																value={newAttributesValues[attribute]}
																onChange={(e) =>
																	setNewAttributesValues({
																		...newAttributesValues,
																		[attribute]: "+" + e,
																	})
																}
															/>
														</>
													);
												} else {
													return (
														<MDBInput
															key={index}
															className="mb-4"
															type="text"
															id={attribute}
															label={t(attribute)}
															value={newAttributesValues[attribute]}
															onChange={(e) =>
																setNewAttributesValues({
																	...newAttributesValues,
																	[attribute]: e.target.value,
																})
															}
														/>
													);
												}
											})}
											{confirmSignInLoading ? (
												<MDBBtn className="mb-4" block disabled>
													<div className="spinner-border" role="status">
														<span className="visually-hidden">{t("Loading...")}</span>
													</div>
												</MDBBtn>
											) : (
												<MDBBtn
													onClick={handleConfirmSignIn}
													className="mb-4"
													block
												>
													{t("Confirm Sign In")}
												</MDBBtn>
											)}
											{error && (
												<div className="alert alert-danger">
													{t(error.message)}
												</div>
											)}
										</>
									) : (
										<form onSubmit={handleSubmit}>
											<br></br>

											<MDBInput
												className="mb-4"
												type="email"
												id="email"
												data-hj-allow
												label={t("Email address")}
												value={username ?? ""}
												onChange={(e) => setUsername(e.target.value)}
											/>
											<MDBInput
												className="mb-4"
												type="password"
												id="password"
												label={t("Password")}
												value={password}
												onChange={(e) => setPassword(e.target.value)}
											/>

											<MDBBtn type="submit" className="" block>
												{t("Sign in")}
											</MDBBtn>
											{error && (
												<div className="alert alert-danger mt-2 text-center">
													{t(error.message)}
												</div>
											)}
											<MDBRow className="my-2">
												{/* <MDBCol className='d-flex justify-content-center'>
                        <MDBCheckbox id='form7Example3' label='Remember me' defaultChecked />
                    </MDBCol> */}
												<MDBCol className="text-center">
													<a
														href="#!"
														onClick={() => setAuthRoute("resetPassword")}
													>
														{t("Forgot password?")}
													</a>
												</MDBCol>
											</MDBRow>

											{/* <div className='text-center mb-0 alert alert-info'>
                        <strong>{t("If you have an account in our old system x4Com, you can migrate it.")}</strong>
                        <br></br>
                    <MDBBtn href='#!' className='m-1' onClick={() => setAuthRoute("migrate")}>{t("Transfer your account")}</MDBBtn>
                    </div> */}

											{/* <div className='text-center'>
                    <p>
                        Not a member? <a href='#!'>Register</a>
                    </p>
                    </div> */}
										</form>
									)}
								</MDBTabsPane>
							</MDBTabsContent>
						</MDBCardBody>
					</MDBCard>
				</MDBCol>

				{/* <MDBCol lg="4" className="d-flex align-items-center justify-content-center" style={{
        height: "100%",
        backgroundSize: "cover",
        backgroundPosition: "center",
        background: "linear-gradient(0deg, rgb(31 50 65 / 80%), rgb(31 50 65 / 39%)) center center / cover, url(https://iec-cp-public.s3.eu-central-1.amazonaws.com/AdobeStock_249789803.jpeg)  center center / cover",
        
        color: "white"
        }}>
        <div>
            <h1>Registrieren</h1>
            <p>
                Erstellen Sie ein Konto, um alle Vorteile zu nutzen.
            </p>
            <MDBBtn href='#!' color='light' className='m-1' style={{color: "black"}} onClick={() => setAuthRoute("signUp")}>Jetzt registrieren</MDBBtn>
        </div>
    </MDBCol> */}
			</MDBRow>
			<LangSelect
				style={{ top: "0.5rem", right: "0.5rem", position: "absolute" }}
				className="langSelect--login"
			/>
			<Footer currentIndustry={currentIndustry} />
		</>
	);
};

export default SignIn;
